import { Button, Icon } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';

import { baseURL } from '../../api/api';
import { Scope, useMicrosoftConfig } from './useMicrosoftConfig';

interface Props {
  scope: Scope;
  returnTo?: string;
  prompt?: 'login';
}

/**
 * Botón "Iniciar sesión con Microsoft". Renderiza solo si el backend reporta
 * `enabled: true` para el `scope` del portal. Hace redirect server-side a
 * `/auth/microsoft-entra-id/authorize` (no usa MSAL — flow 100% redirect).
 */
export default function MicrosoftLoginButton({
  scope,
  returnTo,
  prompt,
}: Props) {
  const { t } = useTranslation();
  const { config, loading } = useMicrosoftConfig(scope);

  if (loading || !config?.enabled) {
    return null;
  }

  const onClick = () => {
    const params = new URLSearchParams({ scope });
    if (returnTo) {
      params.set('returnTo', returnTo);
    }
    if (prompt) {
      params.set('prompt', prompt);
    }
    window.location.assign(
      `${baseURL}/auth/microsoft-entra-id/authorize?${params.toString()}`,
    );
  };

  return (
    <div className="d-flex flex-column">
      <Button
        outlined
        icon={<MicrosoftLogo />}
        text={t('login.microsoftEntraId.button')}
        onClick={onClick}
      />
      <div className="d-flex align-items-start mt-2" style={{ gap: 8 }}>
        <Icon name="information" color="primary" size={32} />
        <small className="text-muted fs-12">
          {t('login.microsoftEntraId.hint')}
        </small>
      </div>
    </div>
  );
}

function MicrosoftLogo() {
  return (
    <svg
      width="21"
      height="21"
      viewBox="0 0 21 21"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ transform: 'translateY(-2px)' }}
    >
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
